import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Clock3, Radio, Trophy, Flame, Building2, Flag as FlagIcon, Shield, ArrowRight, Star } from "lucide-react";
import { PlayerAvatar } from "../PlayerAvatar";
import { TeamBadge } from "../TeamBadge";
import { TEAM_INFO } from "../../lib/teamMap";
import { PHASES, fmtStoryDate } from "../../lib/storiesData";
import { getSettings } from "../../lib/api";

export const GOLD = "#F5C518";
export const teamAbbr = (id) => TEAM_INFO[id]?.abbr || (id || "").toUpperCase();
export const PHASE_ICON = { pre: Clock3, live: Radio, post: Trophy };
export const KIND_ICON = { arena: Building2, referee: FlagIcon, coach: Shield };
export const KIND_LABEL = { player: "Player", team: "Team", coach: "Coach", arena: "Arena", referee: "Official" };

const ICON_PX = { xs: 24, sm: 30, md: 38, lg: 48, xl: 60 };
const FONT_CLASS = { heading: "font-heading", mono: "font-mono-stat", sans: "" };
const DEFAULT_STORY_STYLE = { font: "heading", weight: 800, size: 15, icon: "md" };

// Admin-configurable story card styling (headline font/weight/size + icon size),
// read from global settings so it applies on the Stories page AND the Schedule.
export function useStoryStyle() {
    const { data } = useQuery({ queryKey: ["settings"], queryFn: getSettings, staleTime: 5 * 60 * 1000 });
    const cfg = { ...DEFAULT_STORY_STYLE, ...(data?.stories_card_style || {}) };
    return {
        icon: cfg.icon,
        headline: { fontClass: FONT_CLASS[cfg.font] ?? "font-heading", style: { fontSize: `${cfg.size}px`, fontWeight: cfg.weight } },
    };
}

// Subject avatar — bare logo/photo/icon, NO coloured circle behind it.
export function SubjectAvatar({ subject, size = "md" }) {
    if (subject.kind === "player") return <PlayerAvatar photo={subject.photo_url} name={subject.name} playerId={subject.id} size={size} showTierRing={false} />;
    if (subject.kind === "team") return <TeamBadge teamId={subject.team_id} size={size} asLink={false} />;
    const Icon = KIND_ICON[subject.kind] || Shield;
    const px = ICON_PX[size] || 38;
    return (
        <span className="inline-flex items-center justify-center shrink-0" style={{ width: px, height: px }}>
            <Icon style={{ width: px * 0.72, height: px * 0.72, color: subject.teamColor || GOLD }} />
        </span>
    );
}

export const PhaseTag = ({ phase, size = "sm" }) => {
    const ph = PHASES[phase]; const Icon = PHASE_ICON[phase];
    return (
        <span className={`inline-flex items-center gap-1 rounded-full font-black uppercase tracking-wider ${size === "lg" ? "px-2.5 py-1 text-[11px]" : "px-2 py-0.5 text-[9px]"}`}
            style={{ color: ph.color, background: `${ph.color}1f`, border: `1px solid ${ph.color}55` }} data-testid={`phase-tag-${phase}`}>
            {phase === "live" && <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: ph.color }} /><span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: ph.color }} /></span>}
            <Icon className="h-3 w-3" /> {ph.label}
        </span>
    );
};

export const ExciteMeter = ({ value }) => (
    <div className="flex items-center gap-1.5" title={`Excitement ${value}/100`} data-testid="excite-meter">
        <Flame className="h-3 w-3" style={{ color: GOLD }} />
        <div className="h-1 w-16 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${value}%`, background: `linear-gradient(90deg, ${GOLD}, #FF7A18)` }} /></div>
    </div>
);

export const ContextLine = ({ s }) => {
    if (s.teamId) {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-white/55">
                <TeamBadge teamId={s.teamId} size="xs" asLink={false} />{teamAbbr(s.teamId)}
                {s.oppId && s.oppId !== s.teamId && <><span className="opacity-40">vs</span><TeamBadge teamId={s.oppId} size="xs" asLink={false} />{teamAbbr(s.oppId)}</>}
            </span>
        );
    }
    return <span className="text-[10px] uppercase tracking-wider font-bold text-white/45">{KIND_LABEL[s.subject.kind]} milestone</span>;
};

const wrapLink = (gameId, node, cls) => gameId ? <Link to={`/game/${gameId}`} className={cls}>{node}</Link> : node;

export function StoryCard({ s, testId }) {
    const st = useStoryStyle();
    const inner = (
        <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0E0F16] p-3.5 transition-all duration-300 hover:border-[#F5C518]/40 hover:-translate-y-0.5 shadow-lg shadow-black/40 h-full flex flex-col" data-testid={testId}>
            <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full opacity-[0.18] blur-2xl transition-opacity group-hover:opacity-30" style={{ background: s.teamColor }} />
            <div className="relative flex items-center justify-between gap-2 mb-2.5">
                <div className="flex items-center gap-1.5"><PhaseTag phase={s.phase} /><span className="rounded-full px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider text-white/45 border border-white/10">{KIND_LABEL[s.subject.kind]}</span></div>
                <span className="text-[9px] font-bold uppercase tracking-wider text-white/40">{fmtStoryDate(s.date)}</span>
            </div>
            <div className="relative flex items-start gap-3">
                <SubjectAvatar subject={s.subject} size={st.icon} />
                <div className="min-w-0 flex-1">
                    <h3 className={`${st.headline.fontClass} uppercase tracking-tight text-white leading-tight group-hover:text-[#F5C518] transition-colors`} style={st.headline.style}>{s.headline}</h3>
                    <p className="mt-1 text-[12px] text-white/70 leading-snug line-clamp-2">{s.blurb}</p>
                </div>
            </div>
            <div className="relative mt-auto pt-3 flex items-center justify-between gap-2">
                <ContextLine s={s} />
                <span className="rounded-full px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider text-white/45 border border-white/10">{s.theme}</span>
            </div>
        </div>
    );
    return wrapLink(s.gameId, inner, "block h-full");
}

// Compact card for the Schedule "Stories this week" strip.
export function MiniStory({ s, testId }) {
    const st = useStoryStyle();
    const inner = (
        <div className="group relative overflow-hidden rounded-xl border border-white/10 bg-[#0E0F16] p-2.5 transition-all hover:border-[#F5C518]/40" data-testid={testId}>
            <div className="absolute -right-5 -top-5 h-16 w-16 rounded-full opacity-[0.16] blur-xl" style={{ background: s.teamColor }} />
            <div className="relative flex items-center gap-2.5">
                <SubjectAvatar subject={s.subject} size="sm" />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-0.5"><PhaseTag phase={s.phase} /><span className="text-[8px] font-bold uppercase tracking-wider text-white/35">{fmtStoryDate(s.date)}</span></div>
                    <p className={`${st.headline.fontClass} uppercase tracking-tight text-white leading-tight truncate group-hover:text-[#F5C518] transition-colors`} style={st.headline.style}>{s.headline}</p>
                    {s.blurb && <p className="mt-0.5 text-[11px] text-white/55 leading-snug line-clamp-2">{s.blurb}</p>}
                </div>
            </div>
        </div>
    );
    return wrapLink(s.gameId, inner, "block");
}

export const FeaturedStarTag = () => (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: "#0B0C12", background: GOLD }}><Star className="h-3 w-3" /> Featured Story</span>
);
export const ReadStoryLink = () => (
    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-black text-[#F5C518] group-hover:gap-2 transition-all">Read the story <ArrowRight className="h-3.5 w-3.5" /></span>
);
